import { ConditionOperatorEnumType } from '@reach/shared-types/conditions';
import { generateId } from '../utils/generate-id';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import React from 'react';
import { AddDescriptionButton } from './AddDescriptionButton';
import { AddRelatedResourceCondition } from './AddRelatedResourceCondition';
import { ClauseDescriptionHeadline } from './ClauseDescriptionHeadline';
import { FieldConditionRow } from './FieldConditionRow';
import { LogicPillToggle } from './LogicPillToggle';
import { NestedGroupBlock } from './NestedGroupBlock';
import { RelatedResourceBlock } from './RelatedResourceBlock';
import { buildAddAttributeLabel } from './segment-builder.utils';
import { useSegmentSchemaCatalog } from './SegmentSchemaCatalog';
import type {
  BuilderCondition,
  BuilderFieldCondition,
  BuilderGroup,
  BuilderNestedGroup,
  BuilderRelatedResourceCondition,
} from './segment-builder.types';
import {
  isBuilderFieldCondition,
  isBuilderNestedGroup,
  isBuilderRelatedResourceCondition,
} from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';
import { useSegmentReadOnly } from './segment-viewer-settings-context';

interface ConditionGroupProps {
  group: BuilderGroup;
  groupNumber: number;
  onUpdate: (group: BuilderGroup) => void;
  onDelete: () => void;
  disabled?: boolean;
}

const TOP_LEVEL_GROUP_DEPTH = 1;

export const ConditionGroup: React.FC<ConditionGroupProps> = ({
  group,
  groupNumber,
  onUpdate,
  onDelete,
  disabled = false,
}) => {
  const readOnly = useSegmentReadOnly();
  const canAddDescription = !readOnly && group.description === undefined;
  const { schemaCatalog, getSchemaById } = useSegmentSchemaCatalog();
  if (!schemaCatalog) return null;

  const { contactFields } = schemaCatalog;
  const contactSchema = schemaCatalog.contactSchemaId
    ? getSchemaById(schemaCatalog.contactSchemaId)
    : undefined;
  const contactName = contactSchema?.name ?? 'Contact';
  const addContactConditionLabel = buildAddAttributeLabel(contactName);
  const contactSubject = {
    schemaId: schemaCatalog.contactSchemaId ?? undefined,
    name: contactName,
    displayMetadata: contactSchema?.displayMetadata,
  };
  const isExpanded = group.isExpanded;

  const toggleExpanded = () => {
    onUpdate({ ...group, isExpanded: !group.isExpanded });
  };

  const handleLogicChange = (logic: 'and' | 'or') => {
    onUpdate({ ...group, logic });
  };

  const handleDescriptionChange = (description: string | undefined) => {
    onUpdate({ ...group, description });
  };

  const handleConditionUpdate = (index: number, cond: BuilderCondition) => {
    onUpdate({ ...group, conditions: group.conditions.with(index, cond) });
  };

  const handleConditionDelete = (index: number) => {
    onUpdate({ ...group, conditions: group.conditions.toSpliced(index, 1) });
  };

  const handleAddFieldCondition = () => {
    const firstField = contactFields[0];
    const newCond: BuilderFieldCondition = {
      id: generateId(),
      type: 'field',
      field: firstField?.name ?? '',
      fieldType: firstField?.type ?? 'unknown',
      operator: ConditionOperatorEnumType.EQUALS,
      value: [],
    };
    onUpdate({
      ...group,
      conditions: [...group.conditions, newCond],
    });
  };

  const handleAddNestedGroup = () => {
    // New nested group with opposite logic for intuitive nesting
    const newNestedGroup: BuilderNestedGroup = {
      id: generateId(),
      type: 'nested_group',
      logic: group.logic === 'and' ? 'or' : 'and',
      conditions: [],
      isExpanded: true,
    };
    onUpdate({
      ...group,
      conditions: [...group.conditions, newNestedGroup],
    });
  };

  return (
    <div className={styles.contactWrapper}>
      <div className={styles.contactHeader}>
        <div className={styles.nodeHeaderControls}>
          <button
            type="button"
            className={styles.relatedTitleButton}
            onClick={toggleExpanded}
            disabled={disabled}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse group' : 'Expand group'}>
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          <span className={styles.groupEyebrow}>Group {groupNumber}</span>
        </div>
        <div className={styles.nodeHeaderControls}>
          {canAddDescription && (
            <AddDescriptionButton onClick={() => handleDescriptionChange('')} disabled={disabled} />
          )}
          {!readOnly && (
            <button
              type="button"
              className={styles.groupDeleteBtn}
              onClick={onDelete}
              disabled={disabled}
              aria-label="Delete group">
              Delete
            </button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className={styles.contactBody}>
          {group.description !== undefined && (
            <div className={styles.groupDescription}>
              <ClauseDescriptionHeadline
                variant="group"
                description={group.description}
                onChange={handleDescriptionChange}
                disabled={disabled}
              />
            </div>
          )}

          <div className={styles.conditionsList}>
            {group.conditions.map((cond, i) => (
              <React.Fragment key={cond.id}>
                {i > 0 && (
                  <div className={styles.andConnector}>
                    <LogicPillToggle
                      value={group.logic}
                      onChange={handleLogicChange}
                      disabled={disabled}
                    />
                    <span className={styles.andConnectorLine} />
                  </div>
                )}
                {isBuilderFieldCondition(cond) ? (
                  <FieldConditionRow
                    condition={cond}
                    fieldOptions={contactFields}
                    subject={contactSubject}
                    onUpdate={handleConditionUpdate.bind(null, i)}
                    onDelete={handleConditionDelete.bind(null, i)}
                    disabled={disabled}
                  />
                ) : isBuilderRelatedResourceCondition(cond) ? (
                  <RelatedResourceBlock
                    condition={cond}
                    subject={contactSubject}
                    subjectSchemaId={schemaCatalog.contactSchemaId}
                    onUpdate={handleConditionUpdate.bind(null, i)}
                    onDelete={handleConditionDelete.bind(null, i)}
                    disabled={disabled}
                  />
                ) : isBuilderNestedGroup(cond) ? (
                  <NestedGroupBlock
                    condition={cond}
                    onUpdate={handleConditionUpdate.bind(null, i)}
                    onDelete={handleConditionDelete.bind(null, i)}
                    disabled={disabled}
                    depth={TOP_LEVEL_GROUP_DEPTH + 1}
                  />
                ) : null}
              </React.Fragment>
            ))}
          </div>

          {!readOnly && (
            <div className={styles.addConditionRow}>
              <button
                type="button"
                className={styles.addConditionBtn}
                onClick={handleAddFieldCondition}
                disabled={disabled || contactFields.length === 0}>
                <Plus size={14} />
                {addContactConditionLabel}
              </button>
              <button
                type="button"
                className={styles.addConditionBtn}
                onClick={handleAddNestedGroup}
                disabled={disabled}>
                <Plus size={14} />
                Sub Grouping
              </button>
              <AddRelatedResourceCondition
                subjectSchemaId={schemaCatalog.contactSchemaId}
                addRelatedResourceCondition={(newCond: BuilderRelatedResourceCondition) =>
                  onUpdate({ ...group, conditions: [...group.conditions, newCond] })
                }
                disabled={disabled}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
